import { gridMath } from "./grid";

// The calibration guide: 3×3 cells of the grid, from an anchor cell, that the
// master drags onto a printed grid and resizes by its bottom-right corner.
export const GUIDE_CELLS = 3;
export const MIN_CELL_PX = 8;

const CALIBRATION_KEYS = ["kind", "cell_w", "cell_h", "offset_x", "offset_y", "hex_parity", "cell_distance", "unit", "visible", "style"];

// The draft is drawn in the scene's color, shown or hidden and styled as the scene is until the panel changes that.
export function draftGrid(grid) {
	return { ...calibrationPayload(grid), color: grid.color, visible: grid.visible !== false, style: grid.style ?? "subtle" };
}

// Saving sends only what the panel edits, so the stored color stays as it was.
export function calibrationPayload(grid) {
	return Object.fromEntries(CALIBRATION_KEYS.map((key) => [key, grid[key]]));
}

export function guideAnchor(grid, point) {
	const { col, row } = gridMath(grid).cellAt(point.x, point.y);
	const first = col - 1;
	return { col: grid.kind === "hex" ? first - (first & 1) : first, row: row - 1 };
}

export function guideFrame(grid, anchor) {
	const unit = unitFrame(grid, anchor);
	return {
		x: grid.offset_x + unit.x * grid.cell_w,
		y: grid.offset_y + unit.y * grid.cell_h,
		width: unit.width * grid.cell_w,
		height: unit.height * grid.cell_h,
	};
}

export function moveGrid(grid, dx, dy) {
	return { ...grid, offset_x: round(grid.offset_x + dx), offset_y: round(grid.offset_y + dy) };
}

// A cell keeps its kind's shape: square, or a regular hex (height = width·√3/2).
export function regularHeight(kind, width) {
	return round(width * regularRatio(kind));
}

export function regularWidth(kind, height) {
	return round(height / regularRatio(kind));
}

// The frame's top-left corner stays put while its bottom-right follows `corner`
// projected onto the frame's diagonal, so a stored irregular cell becomes regular.
export function resizeGrid(grid, anchor, corner) {
	const unit = unitFrame(grid, anchor);
	const frame = guideFrame(grid, anchor);
	const ratio = regularRatio(grid.kind);
	const across = unit.width;
	const down = unit.height * ratio;
	const projected = ((corner.x - frame.x) * across + (corner.y - frame.y) * down) / (across * across + down * down);
	const cellW = round(Math.max(projected, MIN_CELL_PX, MIN_CELL_PX / ratio));
	const cellH = regularHeight(grid.kind, cellW);
	return {
		...grid,
		cell_w: cellW,
		cell_h: cellH,
		offset_x: round(frame.x - unit.x * cellW),
		offset_y: round(frame.y - unit.y * cellH),
	};
}

// A printed grid that spans the image: `count` cells across its width.
export function cellsAcross(grid, width, count) {
	if (grid.kind === "hex") {
		const cellW = round(width / (0.75 * count + 0.25));
		return { ...grid, cell_w: cellW, cell_h: regularHeight("hex", cellW), offset_x: 0, offset_y: 0 };
	}
	return { ...grid, cell_w: round(width / count), cell_h: round(width / count), offset_x: 0, offset_y: 0 };
}

// A blank scene's even-parity grid sits half a row lower, as its creation
// places it, so no odd column is cut off at the top.
export function switchParity(grid, parity) {
	if (parity === grid.hex_parity) return grid;
	const shift = ((parity === "even" ? 1 : -1) * grid.cell_h) / 2;
	return { ...grid, hex_parity: parity, offset_y: round(grid.offset_y + shift) };
}

// The frame in cells: a unit grid at the origin, since the frame is linear in
// the cell size and the offsets.
function unitFrame(grid, anchor) {
	const math = gridMath({ ...grid, cell_w: 1, cell_h: 1, offset_x: 0, offset_y: 0 });
	const xs = [];
	const ys = [];
	for (let col = anchor.col; col < anchor.col + GUIDE_CELLS; col++) {
		for (let row = anchor.row; row < anchor.row + GUIDE_CELLS; row++) {
			const points = math.cellPolygon(col, row);
			for (let index = 0; index < points.length; index += 2) {
				xs.push(points[index]);
				ys.push(points[index + 1]);
			}
		}
	}
	const x = Math.min(...xs);
	const y = Math.min(...ys);
	return { x, y, width: Math.max(...xs) - x, height: Math.max(...ys) - y };
}

function regularRatio(kind) {
	return kind === "hex" ? Math.sqrt(3) / 2 : 1;
}

function round(value) {
	return Math.round(value * 100) / 100;
}
